import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box } from "lucide-react";
import { imageSrc } from "../lib/api";
import { curated } from "../lib/curate";
import { feelingName, RAINBOW_ORDER, SECTIONS } from "../lib/sections";
import { RightsLine } from "./Rights";
import { FavouriteMark, StoryMark } from "./FavouriteMark";

const SCROLL_KEY = "afae-catalogue-scroll";

function Placeholder({ title }) {
  return (
    <div data-testid="catalogue-placeholder" className="flex h-full w-full items-center justify-center bg-[#EFE9E2] px-6 text-center">
      <span className="font-display text-xl italic text-[#6B5A7A]">{title}</span>
    </div>
  );
}

// The painting's story, clamped to three lines with a gentle "read more" that opens in place.
function AboutText({ slug, text }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    const check = () => setLong(el.scrollHeight > el.clientHeight + 2);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text]);
  return (
    <>
      <span ref={ref} data-testid={`catalogue-about-${slug}`} className={`mt-2 max-w-md text-center font-display text-[15px] leading-relaxed text-[#6E5C82] ${open ? "" : "line-clamp-3"}`}>
        {text}
      </span>
      {(long || open) && (
        <button data-testid={`catalogue-about-toggle-${slug}`} onClick={() => setOpen((o) => !o)} className="font-display text-sm italic text-[#7E57C2] underline decoration-[#7E57C2]/30 underline-offset-4 transition-colors duration-200 hover:text-[#5B3A85]">
          {open ? "read less" : "read more"}
        </button>
      )}
    </>
  );
}


function Plate({ p, eager, onOpen }) {
  const [state, setState] = useState("loading");
  const ar = p.aspect_ratio || (p.width && p.height ? p.width / p.height : 1);
  const native = p.width || 2400;
  return (
    <figure data-testid={`catalogue-item-${p.slug}`} className="flex flex-col items-center gap-5">
      <button
        data-testid={`painting-card-${p.slug}`}
        onClick={() => onOpen(p)}
        aria-label={`Open ${p.title}`}
        className="group block w-full bg-[#2E2622] p-[3px] shadow-[0_28px_50px_-26px_rgba(40,28,24,0.55),0_4px_10px_-6px_rgba(40,28,24,0.35)] transition-transform duration-500 hover:-translate-y-1"
        style={{ maxWidth: `min(100%, calc(72vh * ${ar} + 46px), ${native + 46}px)` }}
      >
        <span className="block bg-[#FBF8F3] p-[14px] sm:p-5">
          <span className="relative block w-full overflow-hidden bg-[#EFE9E2]" style={{ aspectRatio: `${ar}` }}>
            {state === "error" ? (
              <Placeholder title={p.title} />
            ) : (
              <img
                data-painting-id={p.id}
                src={imageSrc(p.image_url)}
                alt={p.title}
                width={p.width}
                height={p.height}
                loading={eager ? "eager" : "lazy"}
                fetchPriority={eager ? "high" : "auto"}
                decoding="async"
                draggable={false}
                onLoad={() => setState("ready")}
                onError={() => setState("error")}
                className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ${state === "ready" ? "opacity-100" : "opacity-0"}`}
              />
            )}
          </span>
        </span>
      </button>
      <figcaption className="flex flex-col items-center gap-0.5 text-center">
        <span data-testid={`catalogue-title-${p.slug}`} className="font-display text-xl text-[#2D1E3E]">{p.title}</span>
        <span data-testid={`catalogue-word-${p.slug}`} className="font-display text-base italic text-[#6B5A7A]">{p.feeling_word || p.emotion}</span>
        <FavouriteMark painting={p} testid={`catalogue-favourite-${p.slug}`} />
        <StoryMark painting={p} testid={`catalogue-story-${p.slug}`} />
        {p.about?.trim() && <AboutText slug={p.slug} text={p.about} />}
      </figcaption>
    </figure>
  );
}

export default function FallbackGrid({ paintings, webgl, onOpen, onSwitch3D }) {
  const box = useRef(null);
  const ordered = curated(paintings);
  useLayoutEffect(() => {
    const y = +sessionStorage.getItem(SCROLL_KEY) || 0;
    if (box.current && y) box.current.scrollTop = y;
  }, [paintings.length]);
  useEffect(() => {
    const el = box.current;
    const save = () => sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop));
    el.addEventListener("scroll", save, { passive: true });
    return () => el.removeEventListener("scroll", save);
  }, []);
  let first = 0;
  return (
    <div ref={box} data-testid="fallback-grid" className="absolute inset-0 overflow-y-auto bg-[#ECE7E1]">
      <div className="mx-auto max-w-6xl pb-28 pl-14 pr-6 pt-32 sm:px-24 sm:pt-28 md:px-28">
        <header className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 data-testid="fallback-lockup" className="font-display text-4xl italic text-[#2D1E3E] sm:text-5xl">All paintings</h1>
            <p data-testid="fallback-notice" className="mt-4 max-w-md font-display text-lg text-[#5C4A72]">
              {webgl ? "The paintings, gathered as a catalogue. Choose one to step inside." : "Your browser can't open the 3D rooms, so the paintings are gathered here. Choose one to step inside."}
            </p>
          </div>
          {webgl && (
            <button data-testid="switch-to-3d-button" onClick={onSwitch3D} className="flex items-center gap-2 self-start rounded-full border border-[#7E57C2]/40 bg-[#FBF7F2] px-6 py-2.5 font-display text-lg text-[#2D1E3E] transition-colors hover:bg-[#7E57C2] hover:text-white">
              <Box size={16} /> Return to the rooms
            </button>
          )}
        </header>
        <div data-testid="salon-wall" className="flex flex-col gap-24">
          {RAINBOW_ORDER.map((id) => {
            const list = ordered.filter((p) => p.section === id);
            if (!list.length) return null;
            return (
              <section key={id} data-testid={`salon-zone-${id}`} className="flex flex-col gap-12">
                <h2 data-testid={`salon-heading-${id}`} className="border-b border-[#2D1E3E]/10 pb-4 font-display text-3xl italic text-[#2D1E3E]">
                  {feelingName(id)} <span className="text-[#6B5A7A]">— {SECTIONS[id].line}</span>
                </h2>
                <div className="grid grid-cols-1 items-start gap-x-16 gap-y-20 lg:grid-cols-2">
                  {list.map((p) => (
                    <Plate key={p.id} p={p} eager={first++ < 2} onOpen={onOpen} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        <RightsLine testid="rights-line-wall" className="mx-auto mt-16 pb-6 text-center sm:hidden [@media(max-height:560px)]:block" />
      </div>
    </div>
  );
}
